import MessageFormValidateV2 from "components/Common/MessageFormValidateV2";
import {
  useListCollectorHubPrioritySchedule,
  useListRouteCollector,
  useListRouteLocalitiesCollector,
  useListRouteLocalitiesRunner,
  useListRouteRunner,
  useListRunnerHubPrioritySchedule,
  useListTimeSlotCollector,
  useListTimeSlotRunner,
  useMyHubsPriorityScheduleList,
} from "hooks/priority-schedule/usePrioritySchedule";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { Label } from "reactstrap";

// Mapping giữa modelName và hook tương ứng
const MODEL_HOOKS = {
  routeRunner: useListRouteRunner,
  routeLocalitiesRunner: useListRouteLocalitiesRunner,
  timeSlotRunner: useListTimeSlotRunner,
  runnerHub: useListRunnerHubPrioritySchedule,

  routeCollector: useListRouteCollector,
  routeLocalitiesCollector: useListRouteLocalitiesCollector,
  timeSlotCollector: useListTimeSlotCollector,
  collectorHub: useListCollectorHubPrioritySchedule,

  myHubs: useMyHubsPriorityScheduleList,
};

// Mapping giữa modelName và cách format data thành options
const MODEL_FORMAT_OPTIONS = (t, keyValue = "id") => ({
  routeRunner: item => ({
    value: item?.[keyValue],
    label: `${item?.code} - ${item?.name}`,
    data: item,
  }),
  routeLocalitiesRunner: item => ({
    value: item?.[keyValue],
    label: `${item?.district_name} ${
      item?.ward_name ? `- ${item?.ward_name}` : ""
    }`,
    data: item,
  }),
  timeSlotRunner: item => ({
    value: item?.[keyValue],
    label: `${item?.code} (${item?.name})`,
    data: item,
  }),
  runnerHub: item => ({
    value: item?.[keyValue],
    label: `${item?.email} - ${item?.display_name}`,
    data: item,
  }),
  routeCollector: item => ({
    value: item?.[keyValue],
    label: `${item?.code} - ${item?.name}`,
    data: item,
  }),
  routeLocalitiesCollector: item => ({
    value: item?.[keyValue],
    label: `${item?.province_name} - ${item?.district_name}`,
    data: item,
  }),
  timeSlotCollector: item => ({
    value: item?.[keyValue],
    label: `${item?.code} (${item?.name})`,
    data: item,
  }),
  collectorHub: item => ({
    value: item?.[keyValue],
    label: `${item?.email} - ${item?.display_name}`,
    data: item,
  }),
  myHubs: item => ({
    value: item?.[keyValue],
    label: `${item?.name} ${item?.is_center ? " - Center" : " - Hub"}`,
    data: item,
  }),
});

const MODEL_FIELDS = {
  routeRunner: "",
  routeLocalitiesRunner: "",
  timeSlotRunner: "",
  runnerHub: "",

  routeCollector: "",
  routeLocalitiesCollector: "",
  timeSlotCollector: "",
  collectorHub: "",

  myHubs: "",
};

// Custom MenuList component để xử lý infinite scroll
const CustomMenuList = props => {
  const {
    children,
    maxHeight,
    innerRef,
    isLoading,
    onScrollToBottom,
    innerProps,
    getStyles,
  } = props;

  const handleScroll = useCallback(
    e => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      // Trigger sớm hơn một chút để tải trang kế tiếp mượt hơn
      const reachedBottom = scrollTop + clientHeight >= scrollHeight - 8;
      if (reachedBottom && !isLoading) {
        onScrollToBottom();
      }
    },
    [isLoading, onScrollToBottom]
  );

  // Lấy style từ react-select
  const menuStyles = getStyles("menuList", props);

  return (
    <div
      {...innerProps}
      ref={innerRef}
      style={{
        ...menuStyles,
        maxHeight: maxHeight || 260,
        overflowY: "auto",
        overflowX: "hidden",
      }}
      onScroll={handleScroll}
    >
      {children}
      {isLoading && (
        <div style={{ textAlign: "center", padding: "5px" }}>Loading...</div>
      )}
    </div>
  );
};

CustomMenuList.propTypes = {
  children: PropTypes.node,
  maxHeight: PropTypes.any,
  innerRef: PropTypes.func,
  isLoading: PropTypes.bool,
  onScrollToBottom: PropTypes.func,
  innerProps: PropTypes.object,
  getStyles: PropTypes.func,
};

/**
 * @param {object} props
 * @param {object} props.mainForm (required) - Form chính
 * @param {string} props.fieldName (required) - Tên field
 * @param {function} props.t (required) - Hàm dịch
 * @param {boolean} [props.isDisabled] - Disable select
 * @param {boolean} [props.isRequired] - Bắt buộc chọn
 * @param {boolean} [props.isMultiple] - Cho phép chọn nhiều
 * @param {boolean} [props.isLabelAll] - Cho phép chọn tất cả
 * @param {function} [props.onChange] - Hàm xử lý khi thay đổi
 * @param {string} [props.title] - Tiêu đề
 * @param {boolean} [props.isClearable] - Cho phép clear
 * @param {boolean} [props.isAllowCreate] - Cho phép tạo mới
 * @param {"myHubs"|"routeRunner"|"routeLocalitiesRunner"|"timeSlotRunner"|"runnerHub"|"routeCollector"|"routeLocalitiesCollector"|"timeSlotCollector"|"collectorHub"} [props.modelName]
 * @param {any} [props.formatOptionLabel] - Hàm custom label
 * @param {boolean} [props.isPlaintext] - Hiển thị giá trị đã chọn
 * @param {object} [props.customColumnFilter] - Filter custom
 * @param {object} [props.formSelectModelStyle] - Style
 * @param {boolean} [props.isLabel] - Hiển thị label
 * @param {boolean} [props.allowOneOnly] - Chỉ cho phép chọn 1
 * @param {any} [props.keyQuery] - Key query
 * @param {any} [props.menuPlacement] - Vị trí menu
 * @param {any} [props.fieldValueMore] - Giá trị thêm
 * @param {any} [props.expand] - Expand
 * @param {any} [props.customParamFilter] - Custom param filter
 * @param {any} [props.keyValue] - Key value
 * @param {boolean} [props.isUseConfigNotAllowDelete] - Không cho phép xóa
 * @param {boolean} [props.fetchDataWhenSearchOnly] - Chỉ fetch data khi search\
 * @param {string} [props.placeholderSearch] - Placeholder search
 * @param {boolean} [props.isShowTotalCount] - Hiển thị total count
 * @param {boolean} [props.isHaveOptionCreateNew] - Cho phép tạo mới
 * @param {string} [props.labelOptionCreateNew] - Label tạo mới
 */
const FormSelectModel = props => {
  const {
    mainForm,
    fieldName,
    t,
    isDisabled = false,
    isRequired = false,
    isMultiple = false,
    onChange = () => {},
    title = "",
    isClearable = true,
    modelName = "routeRunner",
    formatOptionLabel,
    customColumnFilter,
    isPlaintext = false,
    formSelectModelStyle = {},
    isLabel = true,
    allowOneOnly = false,
    keyQuery = "",
    menuPlacement = "bottom",
    fieldValueMore = {}, // Pass các field value more để thêm vào data
    isAllowCreate = false,
    expand = "",
    isLabelAll = false, // option choose all
    isHaveOptionCreateNew = false,
    labelOptionCreateNew = t(`form.create_new`),
    customParamFilter = {},
    keyValue = "id",
    isUseConfigNotAllowDelete = true,
    fetchDataWhenSearchOnly = false,
    placeholderSearch = "", // Placeholder search
    isShowTotalCount = false,
  } = props;

  const {
    formState: { errors },
    watch,
    setValue,
    control,
  } = mainForm;

  const OPTION_CUSTOM = {
    ALL: { value: "__ALL__", label: t(`form.all`) },
    CREATE_NEW: {
      value: "__CREATE_NEW__",
      label: labelOptionCreateNew,
      data: { isCreateNew: true },
    },
  };

  const [searchKeyword, setSearchKeyword] = useState("");
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const GET_MODEL_KEY_WORDS = useCallback(
    inputKeyword => {
      const input = inputKeyword?.trim()?.replace(/\s+/g, " ");
      return {
        keywords: input || undefined,
      };
    },
    [modelName]
  );

  const hasMoreRef = useRef(true);
  const searchTimeoutRef = useRef(null);
  const [allLoadedItems, setAllLoadedItems] = useState([]);
  const isLoadingMoreRef = useRef(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isNeedFetchData, setIsNeedFetchData] = useState(true);

  // Watch value from form
  const formValue = watch(fieldName);

  // Lấy hook tương ứng với modelName
  const useModelHook = MODEL_HOOKS[modelName] || useListRouteRunner;

  const initModelPayloadParams = {
    page: 1,
    sizePerPage: 20,
    sort: "",
    keywords: "",
    columnFilter: {
      ...customColumnFilter,
      "filter[record_status]": "O",
    },
    columnFilterKeep: {
      "filter[record_status]": "O",
    },
    fields: MODEL_FIELDS[modelName] || "",
    expand: expand || "",
    run: false,
    columnsVisible: {},
    ...customParamFilter,
  };

  const [modelPayload, setModelPayload] = useState({
    key: keyQuery || fieldName,
    params: initModelPayloadParams,
  });

  const modelQuery = useModelHook({
    payload: modelPayload,
    onSuccess: response => {
      if (isMultiple) {
        if (
          formValue?.find(item => item?.isNeedToFindByFieldName) &&
          response?.items?.length === 0
        ) {
          // console.log("🚀 setValue2:");
          setValue(fieldName, []);
        }
      } else {
        if (
          formValue?.isNeedToFindByFieldName &&
          response?.items?.length === 0
        ) {
          // console.log("🚀 setValue3:");
          setValue(fieldName, null);
        }
      }
    },
    onError: () => {},
  });

  const {
    data: modelData,
    remove: removeModelQuery,
    isFetching: isFetchingModelQuery,
  } = modelQuery;

  const { items: modelItems, meta: modelMeta } = modelData || {
    items: [],
    meta: {},
  };

  // Khi có dữ liệu mới từ API, thêm vào danh sách đã tải
  useEffect(() => {
    if (modelItems) {
      if (modelPayload.params.page === 1) {
        // Nếu là trang đầu tiên hoặc tìm kiếm mới, ghi đè lên danh sách cũ
        setAllLoadedItems(modelItems);
      } else {
        // Nếu là trang tiếp theo, thêm vào danh sách đã có
        setAllLoadedItems(prevItems => {
          const newItemIds = new Set(modelItems.map(item => item[keyValue]));
          // Lọc ra các items cũ không trùng với items mới
          const filteredPrevItems = prevItems.filter(
            item => !newItemIds.has(item[keyValue])
          );
          // Kết hợp items cũ (không trùng) với items mới
          return [...filteredPrevItems, ...modelItems];
        });
      }
      const hasMore = modelMeta?.currentPage < modelMeta?.pageCount;

      hasMoreRef.current = hasMore;
      isLoadingMoreRef.current = false;
    }
  }, [modelData]);

  // Lấy hàm format options cho model hiện tại
  const getFormatFunction = useCallback(() => {
    if (formatOptionLabel) return formatOptionLabel;
    return (
      MODEL_FORMAT_OPTIONS(t, keyValue)[modelName] ||
      MODEL_FORMAT_OPTIONS(t, keyValue).contractType
    );
  }, [modelName, formatOptionLabel, t, keyValue]);

  // Cập nhật selectedValue từ formValue khi có dữ liệu từ allLoadedItems
  useEffect(() => {
    if (formValue && allLoadedItems.length > 0) {
      if (isMultiple && Array.isArray(formValue)) {
        // Nếu là multi-select
        const formatFunc = getFormatFunction();
        const selectedOptions = formValue.map(selectedItem => {
          // Tìm item tương ứng trong allLoadedItems
          const item = allLoadedItems.find(
            loadedItem => loadedItem[keyValue] === selectedItem.value
          );
          if (item) {
            return formatFunc(item);
          } else {
            // Nếu không tìm thấy trong dữ liệu đã tải, giữ nguyên giá trị form
            return selectedItem;
          }
        });
        setSelectedValue(selectedOptions);
      } else if (!isMultiple && formValue) {
        // Nếu là single-select
        const formatFunc = getFormatFunction();
        const item = allLoadedItems.find(
          loadedItem => loadedItem[keyValue] === formValue.value
        );
        if (item) {
          setSelectedValue(formatFunc(item));
        } else {
          // Nếu không tìm thấy trong dữ liệu đã tải, giữ nguyên giá trị form
          setSelectedValue(formValue);
        }
      }
    }
  }, [formValue, allLoadedItems, isMultiple, getFormatFunction]);

  // Format data to options
  const formatDataToOptions = useCallback(
    items => {
      if (!items || !Array.isArray(items)) return [];

      const formatFunc = getFormatFunction();
      return items.map(formatFunc);
    },
    [getFormatFunction]
  );

  // Memoize options để tránh re-render không cần thiết
  const formattedOptions = useMemo(() => {
    let formatData = formatDataToOptions(allLoadedItems);
    // console.log("🚀 ~ FormSelectModel ~ formatData:", formatData);
    // formatData = formatData?.map((item, index) => {
    //   return {
    //     ...item,
    //     label: `${index + 1}. ${item.label}`,
    //   };
    // });
    if (isLabelAll === true && formatData?.length > 0) {
      return [OPTION_CUSTOM.ALL, ...formatData];
    }
    if (isHaveOptionCreateNew === true) {
      return [OPTION_CUSTOM.CREATE_NEW, ...formatData];
    }
    return formatData;
  }, [allLoadedItems, formatDataToOptions]);

  // Xử lý khi input thay đổi
  const handleInputChange = useCallback(
    (inputValue, { action }) => {
      if (action === "input-change") {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
          setSearchKeyword(inputValue);
          const MODEL_KEYWORDS = GET_MODEL_KEY_WORDS(inputValue);
          setModelPayload(prev => ({
            ...prev,
            params: {
              ...prev.params,
              page: 1,
              columnFilter: {
                ...initModelPayloadParams.columnFilter,
              },
              keywords: MODEL_KEYWORDS?.keywords,
              run: true,
            },
          }));
        }, 500);
      }
      return inputValue;
    },
    [setModelPayload, GET_MODEL_KEY_WORDS]
  );

  // Xử lý khi scroll đến cuối dropdown
  const handleMenuScrollToBottom = useCallback(() => {
    if (hasMoreRef.current && !isLoadingMoreRef.current) {
      // console.log("🚀 ~ FormSelectModel ~ handleMenuScrollToBottom:");
      isLoadingMoreRef.current = true;
      const MODEL_KEYWORDS = GET_MODEL_KEY_WORDS(searchKeyword);
      if (!fetchDataWhenSearchOnly) {
        setModelPayload(prev => ({
          ...prev,
          params: {
            ...prev.params,
            page: prev.params.page + 1,
            ...MODEL_KEYWORDS,
          },
        }));
      }
    }
  }, [setModelPayload, searchKeyword, GET_MODEL_KEY_WORDS]);

  // Xử lý khi mở menu dropdown
  const handleMenuOpen = useCallback(() => {
    // console.log("🚀 ~ FormSelectModel ~ handleMenuOpen:", {
    //   allLoadedItems,
    //   isNeedFetchData,
    //   searchKeyword,
    // });
    // Reset về page 1 và keyword trống khi mở dropdown mới
    setIsOpenMenu(true);
    setSearchKeyword("");
    if (
      allLoadedItems?.length === 0 ||
      isNeedFetchData ||
      searchKeyword !== ""
    ) {
      setIsNeedFetchData(false);
      if (!fetchDataWhenSearchOnly) {
        setModelPayload(prev => ({
          ...prev,
          params: {
            ...initModelPayloadParams,
            run: fetchDataWhenSearchOnly ? false : true,
          },
        }));
      }
    }
  }, [allLoadedItems, isNeedFetchData, searchKeyword, setModelPayload]);

  const handleMenuClose = useCallback(() => {
    setIsOpenMenu(false);
  }, []);

  // Xử lý khi chọn một option
  const handleChange = useCallback(
    (dataChange, actionMeta) => {
      console.log("🚀 ~ handleChange :", {
        actionMeta,
        dataChange,
        searchKeyword,
      });
      const { action, name, removedValue, removedValues } = actionMeta;
      let selectedOption = dataChange;
      if (
        action === "remove-value" &&
        removedValue?.data?.isNotAllowDelete &&
        isUseConfigNotAllowDelete &&
        isMultiple
      ) {
        return;
      }
      if (action === "clear" && isMultiple) {
        selectedOption = removedValues?.filter(
          item => item?.data?.isNotAllowDelete && isUseConfigNotAllowDelete
        );
      }
      if (searchKeyword !== "") {
        setIsNeedFetchData(true);
      }
      // Xử lý chọn option tất cả không chọn được option khác
      if (isLabelAll === true && isMultiple) {
        const selectedValues = selectedOption.map(item => item.value);
        if (selectedValues.includes(OPTION_CUSTOM.ALL.value)) {
          const indexOfAllValue = selectedValues.indexOf(
            OPTION_CUSTOM.ALL.value
          );
          let finalValue = [];
          if (selectedOption?.length === 1 || indexOfAllValue !== 0) {
            finalValue = [OPTION_CUSTOM.ALL];
          } else {
            finalValue = selectedOption.slice(indexOfAllValue + 1);
          }
          // console.log("🚀 setValue4:", finalValue);
          setValue(fieldName, finalValue, { shouldValidate: true });
          return;
        }
      }

      if (isMultiple && allowOneOnly) {
        // If allowOneOnly is true, we only keep the most recently selected option
        if (
          selectedOption &&
          Array.isArray(selectedOption) &&
          selectedOption.length > 0
        ) {
          // Get the last selected option (most recent one)
          const lastSelected = selectedOption[selectedOption.length - 1];
          // Replace the entire selection with just this one option
          selectedOption = [lastSelected];
        }
      }

      setSelectedValue(selectedOption);

      if (onChange) {
        onChange(selectedOption);
      }

      if (mainForm && fieldName) {
        let finalValue = selectedOption;
        if (isMultiple && fieldValueMore) {
          finalValue = selectedOption?.map(item => ({
            ...item,
            ...fieldValueMore,
          }));
        }
        // console.log("🚀 setValue5:", finalValue);
        setValue(fieldName, finalValue, { shouldValidate: true });
      }

      // Reset search
      setSearchKeyword("");
    },
    [mainForm, fieldName, onChange, searchKeyword]
  );

  const selectComponents = useMemo(
    () => ({
      MenuList: props => (
        <CustomMenuList
          {...props}
          isLoading={isLoadingMoreRef.current}
          onScrollToBottom={handleMenuScrollToBottom}
        />
      ),
    }),
    [handleMenuScrollToBottom, isMultiple]
  );

  // START Trigger load khi cần tìm value theo field name
  useEffect(() => {
    if (isMultiple) {
      if (formValue?.[0]?.isNeedToFindByFieldName) {
        setModelPayload({
          ...modelPayload,
          params: {
            ...modelPayload.params,
            keywords: undefined,
            columnFilter: {
              ...modelPayload.params.columnFilter,
              [`filter[${formValue?.[0]?.isNeedToFindByFieldName}][in]`]:
                formValue.map(item => item.value),
            },
            run: true,
          },
        });
      }
    } else {
      if (formValue?.isNeedToFindByFieldName) {
        setModelPayload({
          ...modelPayload,
          params: {
            ...modelPayload.params,
            keywords: undefined,
            columnFilter: {
              ...modelPayload.params.columnFilter,
              [`filter[${formValue.isNeedToFindByFieldName}]`]: formValue.value,
            },
            run: true,
          },
        });
      }
    }
  }, [formValue]);

  useEffect(() => {
    if (isMultiple) {
      if (formValue?.length === 0 || modelItems?.length === 0) {
        return;
      }
      const fieldNameFind = formValue?.[0]?.isNeedToFindByFieldName;
      if (fieldNameFind) {
        let resultFindByFieldName = modelItems;
        if (resultFindByFieldName?.length > 0) {
          resultFindByFieldName = resultFindByFieldName?.map(item => {
            // Set thêm giá trị mặc định của formValue nếu có
            const findByFieldName = formValue.find(
              f => f?.value === item?.[fieldNameFind]
            );
            if (findByFieldName) {
              delete findByFieldName.isNeedToFindByFieldName;
              delete findByFieldName.value;
            }
            return {
              ...item,
              ...findByFieldName,
            };
          });
          const dataFormat = formatDataToOptions(resultFindByFieldName);
          // console.log("🚀 ~ dataFormat:", dataFormat);
          // console.log("🚀 setValue 1:", dataFormat);
          setValue(fieldName, dataFormat);
        }
      }
    } else {
      if (!formValue || modelItems?.length === 0) {
        return;
      }
      const fieldNameFind = formValue?.isNeedToFindByFieldName;
      if (fieldNameFind) {
        let resultFindByFieldName = modelItems;
        if (resultFindByFieldName?.length > 0) {
          const dataFormat = formatDataToOptions(resultFindByFieldName)?.[0];
          // console.log("🚀 ~ dataFormat:", dataFormat);
          // console.log("🚀 setValue6:", dataFormat);
          setValue(fieldName, dataFormat);
        }
      }
    }
  }, [modelItems, formValue, setValue]);
  // END Trigger load khi cần tìm value theo field name

  // Cleanup timeout khi component unmount
  useEffect(() => {
    return () => {
      removeModelQuery();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const selectValueTranslate = useMemo(() => {
    if (!formValue) return null;

    const formatFunc = getFormatFunction();
    if (isMultiple && Array.isArray(formValue)) {
      if (formValue.length === 0) {
        return [];
      }
      if (formValue.some(item => item?.value === OPTION_CUSTOM.ALL.value)) {
        return formValue;
      }
      const dataFormat = formValue.map(item => {
        if (item?.data?.isCreateNew) {
          return OPTION_CUSTOM.CREATE_NEW;
        }
        // Chỉ format lại label dựa trên data có sẵn trong formValue
        if (item.data) {
          return formatFunc(item.data);
        }
      });
      return dataFormat;
    }

    if (!isMultiple && formValue) {
      if (formValue?.value === OPTION_CUSTOM.ALL.value) {
        return formValue;
      }
      if (formValue?.data?.isCreateNew) {
        return OPTION_CUSTOM.CREATE_NEW;
      }
      // Chỉ format lại label dựa trên data có sẵn trong formValue
      if (formValue.data) {
        const dataTranslateReload = formatFunc(formValue.data);
        return dataTranslateReload;
      }
    }

    return formValue;
  }, [formValue, isMultiple, getFormatFunction]);

  const renderPlaintextTranslate = useMemo(() => {
    if (isMultiple && Array.isArray(selectValueTranslate)) {
      return selectValueTranslate.map(item => item?.label).join(", ");
    }
    if (!isMultiple && selectValueTranslate) {
      return selectValueTranslate?.label;
    }
    return "";
  }, [selectValueTranslate, isMultiple]);

  // Custom styles for single select to show full content without ellipsis
  const selectStyles = useMemo(() => {
    return {
      singleValue: provided => ({
        ...provided,
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        maxWidth: "100%",
      }),
      valueContainer: provided => ({
        ...provided,
        overflow: "visible",
        width: "100%",
      }),
      control: provided => ({
        ...provided,
        overflow: "visible",
      }),
      menu: provided => ({
        ...provided,
        overflow: "hidden",
      }),
      menuList: provided => ({
        ...provided,
        maxHeight: 260,
        overflowY: "auto",
      }),
    };
  }, []);

  const numberOfItemsSelected = useMemo(() => {
    if (isMultiple && Array.isArray(formValue)) {
      return formValue.length;
    }
    return 1;
  }, [formValue, isMultiple]);

  return (
    <div style={{ ...formSelectModelStyle }} className={`${keyQuery} mb-1`}>
      {/* key:{keyQuery} */}
      {isLabel && (
        <div className="d-flex align-items-center gap-2">
          <Label style={{ marginBottom: isPlaintext ? "0px" : "0.5rem" }}>
            {title || t(`form.${fieldName}`)}
            {isOpenMenu && isShowTotalCount && modelMeta?.totalCount ? (
              <span className="text-muted">
                {" "}
                ({modelMeta?.totalCount - numberOfItemsSelected || 0}){" "}
              </span>
            ) : null}
            {isRequired && <span className="text-danger"> *</span>}
          </Label>
        </div>
      )}
      {isPlaintext ? (
        <div className="form-control-plaintext">{renderPlaintextTranslate}</div>
      ) : (
        <Controller
          control={control}
          name={fieldName}
          render={({ field }) => {
            return (
              <Select
                {...field}
                inputId={fieldName}
                value={selectValueTranslate || field.value}
                options={formattedOptions}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onInputChange={handleInputChange}
                isDisabled={isDisabled}
                isClearable={isClearable}
                menuPlacement={menuPlacement}
                maxMenuHeight={260}
                isMulti={isMultiple}
                closeMenuOnSelect={isMultiple ? false : true}
                // menuIsOpen={true}
                placeholder={
                  placeholderSearch ||
                  t("common:placeholder.select", {
                    title: title || t(`form.${fieldName}`),
                  })
                }
                onChange={handleChange}
                isLoading={isFetchingModelQuery}
                noOptionsMessage={() => {
                  return isFetchingModelQuery
                    ? t("common:message.loading")
                    : fetchDataWhenSearchOnly
                    ? placeholderSearch
                    : t("common:message.no_options");
                }}
                onMenuScrollToBottom={handleMenuScrollToBottom}
                components={selectComponents}
                classNamePrefix="select2-selection"
                className={errors[fieldName] ? "is-invalid" : ""}
                styles={selectStyles}
              />
            );
          }}
        />
      )}
      <MessageFormValidateV2 errors={errors} fieldName={fieldName} />
    </div>
  );
};

export default FormSelectModel;
